// ─────────────────────────────────────────────────────────────────────────────
// PreferredProductModalComponent
// ─────────────────────────────────────────────────────────────────────────────
// Dialog for creating and editing preferred-product entries.
// Customer + product identify the unique pair: both are required on create
// and locked on edit — only purchase frequency stays editable.
// On a duplicate-pair create the effects show a toast and the dialog stays
// open (it only closes on a success action).
// ─────────────────────────────────────────────────────────────────────────────

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

import { AppState } from 'src/app/core/app.state';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { SharedDialogComponent } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { CustomersService } from 'src/app/features/customers/services/customers.services';
import { ProductsService } from 'src/app/features/produits/services/produits.services';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';

import { ICreatePreferredProductPayload, IPreferredProduct, IUpdatePreferredProductPayload } from '../../models/preferred-product.models';
import {
  CreatePreferredProduct,
  CreatePreferredProductSuccess,
  UpdatePreferredProduct,
  UpdatePreferredProductSuccess,
} from '../../store/preferred-products.actions';
import { selectPreferredProductCreating, selectPreferredProductUpdating } from '../../store/preferred-products.selectors';

interface SelectOption {
  value: number;
  label: string;
}

@Component({
  selector: 'app-preferred-product-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, SharedModule, UiModule, SharedDialogComponent],
  templateUrl: './preferred-product-modal.component.html',
  styleUrls: ['./preferred-product-modal.component.scss'],
})
export class PreferredProductModalComponent implements OnInit, OnDestroy {
  // ── Form ──────────────────────────────────────────────────────────────────

  form!: FormGroup;
  customerSearchControl = new FormControl('');
  productSearchControl = new FormControl('');

  // ── State ─────────────────────────────────────────────────────────────────

  isUpdate = false;
  canSubmit = false;
  canSubmit$: Observable<boolean> = of(false);
  submitting$: Observable<boolean> = of(false);

  // ── Autocomplete state ────────────────────────────────────────────────────

  filteredCustomers$!: Observable<SelectOption[]>;
  filteredProducts$!: Observable<SelectOption[]>;
  customerSearchState: 'idle' | 'searching' | 'results' = 'idle';
  productSearchState: 'idle' | 'searching' | 'results' = 'idle';

  // ── Cleanup ───────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<PreferredProductModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IPreferredProduct | null,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private actions$: Actions,
    private customersService: CustomersService,
    private productsService: ProductsService,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.initForm();
    this.initCustomerAutocomplete();
    this.initProductAutocomplete();
    this.initClearDetection();
    this.handleEditMode();

    this.canSubmit$ = this.store.select(selectCanDo('knowledge_base', this.isUpdate ? 'edit' : 'create'));
    this.canSubmit$.pipe(takeUntil(this.destroy$)).subscribe((canSubmit) => {
      this.canSubmit = canSubmit;
      if (canSubmit) {
        this.form.get('purchase_frequency')?.enable({ emitEvent: false });
        if (!this.isUpdate) {
          this.form.get('customer_id')?.enable({ emitEvent: false });
          this.form.get('product_id')?.enable({ emitEvent: false });
          this.customerSearchControl.enable({ emitEvent: false });
          this.productSearchControl.enable({ emitEvent: false });
        }
      } else {
        this.form.disable({ emitEvent: false });
        this.customerSearchControl.disable({ emitEvent: false });
        this.productSearchControl.disable({ emitEvent: false });
      }
    });

    this.submitting$ = combineLatest([this.store.select(selectPreferredProductCreating), this.store.select(selectPreferredProductUpdating)]).pipe(
      map(([creating, updating]) => creating || updating),
    );

    this.listenForSuccess();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Initialization ────────────────────────────────────────────────────────

  private initForm(): void {
    this.form = this.fb.group({
      customer_id: [null, [Validators.required]],
      product_id: [null, [Validators.required]],
      purchase_frequency: [null, [Validators.min(1)]],
    });
  }

  private initCustomerAutocomplete(): void {
    this.filteredCustomers$ = this.customerSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => {
        const keyword = typeof term === 'string' ? term : '';
        if (!keyword || keyword.length < 2) {
          this.customerSearchState = 'idle';
          return of([] as SelectOption[]);
        }
        this.customerSearchState = 'searching';
        return this.customersService.listCustomers({ number: 1, size: 20 }, undefined, keyword).pipe(
          map((res) => {
            const options: SelectOption[] = (res.data ?? []).map((c) => ({
              value: c.id,
              label: c.name,
            }));
            this.customerSearchState = 'results';
            return options;
          }),
        );
      }),
      takeUntil(this.destroy$),
    );
  }

  private initProductAutocomplete(): void {
    this.filteredProducts$ = this.productSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => {
        const keyword = typeof term === 'string' ? term : '';
        if (!keyword || keyword.length < 2) {
          this.productSearchState = 'idle';
          return of([] as SelectOption[]);
        }
        this.productSearchState = 'searching';
        return this.productsService.fetchProductsBySearch({ number: 1, size: 20 }, { filters: [] }, keyword, undefined, false, true).pipe(
          map((res) => {
            const options: SelectOption[] = (res.data ?? [])
              .filter((p) => p.id != null)
              .map((p) => ({
                value: p.id!,
                label: p.sku ? `${p.name} (${p.sku})` : p.name,
              }));
            this.productSearchState = 'results';
            return options;
          }),
        );
      }),
      takeUntil(this.destroy$),
    );
  }

  private initClearDetection(): void {
    this.customerSearchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (typeof value === 'string' && !value) {
        this.form.patchValue({ customer_id: null });
      }
    });

    this.productSearchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (typeof value === 'string' && !value) {
        this.form.patchValue({ product_id: null });
      }
    });
  }

  private handleEditMode(): void {
    if (!this.data) return;

    this.isUpdate = true;

    this.form.patchValue({
      customer_id: this.data.customer_id,
      product_id: this.data.product_id,
      purchase_frequency: this.data.purchase_frequency,
    });

    if (this.data.customer_name) {
      this.customerSearchControl.setValue({ value: this.data.customer_id, label: this.data.customer_name } as unknown as string);
    }
    if (this.data.product_name) {
      const label = this.data.sku ? `${this.data.product_name} (${this.data.sku})` : this.data.product_name;
      this.productSearchControl.setValue({ value: this.data.product_id, label } as unknown as string);
    }

    // Pair identity is immutable -- only purchase frequency stays editable
    this.form.get('customer_id')?.disable({ emitEvent: false });
    this.form.get('product_id')?.disable({ emitEvent: false });
    this.customerSearchControl.disable({ emitEvent: false });
    this.productSearchControl.disable({ emitEvent: false });
  }

  private listenForSuccess(): void {
    this.actions$
      .pipe(ofType(CreatePreferredProductSuccess, UpdatePreferredProductSuccess), take(1), takeUntil(this.destroy$))
      .subscribe(() => this.close());
  }

  // ── Autocomplete handlers ─────────────────────────────────────────────────

  onCustomerSelected(option: SelectOption): void {
    this.form.patchValue({ customer_id: option.value });
  }

  onProductSelected(option: SelectOption): void {
    this.form.patchValue({ product_id: option.value });
  }

  clearCustomer(): void {
    this.customerSearchControl.setValue('');
    this.form.patchValue({ customer_id: null });
  }

  clearProduct(): void {
    this.productSearchControl.setValue('');
    this.form.patchValue({ product_id: null });
  }

  displayOptionFn = (option: SelectOption | string): string => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.label ?? '';
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  submitPermission() {
    return { module: 'knowledge_base', action: this.isUpdate ? 'edit' : 'create' };
  }

  save(): void {
    if (!this.canSubmit) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting$.pipe(take(1), takeUntil(this.destroy$)).subscribe((isSubmitting) => {
      if (isSubmitting) return;

      const raw = this.form.getRawValue();
      const frequency = raw.purchase_frequency != null && raw.purchase_frequency !== '' ? Number(raw.purchase_frequency) : null;

      if (this.isUpdate && this.data) {
        const payload: IUpdatePreferredProductPayload = {
          purchase_frequency: frequency ?? 1,
        };
        this.store.dispatch(UpdatePreferredProduct({ id: this.data.id, payload }));
      } else {
        const payload: ICreatePreferredProductPayload = {
          customer_id: raw.customer_id,
          product_id: raw.product_id,
        };
        if (frequency != null) {
          payload.purchase_frequency = frequency;
        }
        this.store.dispatch(CreatePreferredProduct({ payload }));
      }
    });
  }

  // ── Close ─────────────────────────────────────────────────────────────────

  close(): void {
    this.dialogRef.close();
  }
}
